import AppContext from "@/AppContext";
import LucidMascot from "@/assets/images/lucid-mascot.png";
import startupMatch from "@/assets/images/match-startup.webp";
import startupBenefits from "@/assets/json/startup-benefits.json";
import tired from "@/assets/images/tired.webp";
import ai_platform from "@/assets/images/ai-platform.webp";
import backgroundLogo from "@/assets/images/background-logo.webp";
import logo1 from "@/assets/logos/startup-logo1.svg";
import logo2 from "@/assets/logos/startup-logo2.svg";
import logo3 from "@/assets/logos/startup-logo3.png";
import logo4 from "@/assets/logos/startup-logo4.svg";
import logo5 from "@/assets/logos/startup-logo5.svg";
import videoCover from "@/assets/images/phone-frame.webp";
import { loginPopup, purchasePopup, purchaseStartupPopup } from "@/Atoms";
import Layout from "@/components/elements/Layout";
import OnUpgradeAtStripeCheckout from "@/components/pricing/OnUpgradeAtStripeCheckout";

import InvestorCard from "@/components/investor/card";
import FreeTrialStartup from "@/components/modal/FreeTrialStartup";
import FundraisingReadinessBanner, {
  FUNDRAISING_READINESS_TEST_URL,
} from "@/components/parts/FundraisingReadinessBanner";
import StartupCard from "@/components/startup/new_card";
import { Button } from "@/components/ui/button";
import useAuth from "@/hooks/auth";
import availablePricesDev from "@/hooks/availablePrices.dev";
import availablePricesMonthlyDev from "@/hooks/availablePrices.monthly.dev";
import availablePricesMonthlyProd from "@/hooks/availablePrices.monthly.prod";
import availablePricesProd from "@/hooks/availablePrices.prod";
import { getSEOByPathname } from "@/services/api/SEO";
import {
  checkStartupExistNoLogin,
  getStartupList,
  searchStartup,
} from "@/services/api/Startup";
import { checkCoupon } from "@/services/api/User";
import { searchInvestor } from "@/services/api/VentureCapital";
import {
  checkEmailDomainWithList,
  isValidDomain,
} from "@/utils/checkEmailDomain";
import { X } from "@phosphor-icons/react";
import {
  ArrowRight,
  Barbell,
  Brain,
  CalendarCheck,
  ChartBar,
  ChartPieSlice,
  ChartScatter,
  Check,
  CheckCircle,
  Eyes,
  Files,
  FolderLock,
  GlobeHemisphereEast,
  GlobeHemisphereWest,
  HandCoins,
  Handshake,
  MagnifyingGlass,
  MagnifyingGlassMinus,
  MapPinSimpleArea,
  Person,
  Play,
  Rocket,
  RocketLaunch,
  Sparkle,
  SpeakerHigh,
  Target,
} from "@phosphor-icons/react/dist/ssr";
import axios from "axios";
import { useAtom } from "jotai";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { convertStripeAmount } from "@/utils/currency";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import FeatureItem from "@/components/FeatureItem";

const ReactPlayer = dynamic(() => import("react-player/lazy"), { ssr: false });

const CarouselReport = dynamic(
  () => import("@/components/parts/CarouselReport"),
  {
    ssr: false,
  }
);

const CarouselStartup = dynamic(
  () => import("@/components/parts/CarouselStartup"),
  {
    ssr: false,
  }
);

const StartupIntro = ({
  userLocation,
  priceData,
  priceDataMonthly,
  priceIds,
  currencyCode,
  ip,
  seoData,
}) => {
  const { user } = useAuth();
  const router = useRouter();
  const ecosystemRef = useRef(null);
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [playing, setIsPlaying] = useState(false);
  const [openLoginPopup, setOpenLoginPopup] = useAtom(loginPopup);
  const [openPurchasePopup, setOpenPurchasePopup] = useAtom(purchasePopup);
  const [openPurchaseStartupPopup, setOpenPurchaseStartupPopup] =
    useAtom(purchaseStartupPopup);
  const { state: _state } = useContext(AppContext);
  const { planSubscription, loading } = _state;
  const [state, setState] = useState(null);
  const [startups, setStartups] = useState([]);
  const [specialReports, setSpecialReports] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isPurchasePopup, setIsPurchasePopup] = useState(false);
  const [startingQuestion, setStartingQuestion] = useState("q0");

  // Startup carousel data states
  const [topFundedStartups, setTopFundedStartups] = useState([]);
  const [startupFacets, setStartupFacets] = useState({ country: [] });
  const [selectedCountry, setSelectedCountry] = useState(null);

  // Startup database section states
  const [featuredStartups, setFeaturedStartups] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [startupKeyword, setStartupKeyword] = useState("");
  const [investorKeyword, setInvestorKeyword] = useState("");
  const [keyword, setKeyword] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchingStartups, setIsSearchingStartups] = useState(false);
  const [isSearchingInvestors, setIsSearchingInvestors] = useState(false);
  const [userWishlist, setUserWishlist] = useState([]);
  const [userWishlistIds, setUserWishlistIds] = useState([]);
  const [wishlistCategories, setWishlistCategories] = useState(null);

  // StartupCheckout modal state
  const [isStartupCheckoutOpen, setIsStartupCheckoutOpen] = useState(false);

  // Direct form modal state (similar to angel investors)
  const [isDirectFormOpen, setIsDirectFormOpen] = useState(false);

  // Startup validation states
  const [startupExist, setStartupExist] = useState(false);
  const [foundStartup, setFoundStartup] = useState(null);
  const [isSameUserEmailAndWebsite, setIsSameUserEmailAndWebsite] =
    useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [selectedPlan, setSelectedPlan] = useState("startup");
  const [isCoupon, setIsCoupon] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [inputCoupon, setInputCoupon] = useState("");
  const [couponError, setCouponError] = useState(false);
  const [couponErrorProduct, setCouponErrorProduct] = useState(false);
  const [loadingCheck, setLoadingCheck] = useState(false);
  const [billingCycle, setBillingCycle] = useState("yearly");
  const [currentPriceData, setCurrentPriceData] = useState({});
  const [emailPayloadForGuest, setEmailPayloadForGuest] = useState("");
  const [fullNamePayloadForGuest, setFullNamePayloadForGuest] = useState("");
  const [phonePayloadForGuest, setPhonePayloadForGuest] = useState("");

  const features = [
    {
      icon: Barbell,
      title: "Tactical Founder Training",
      description:
        "Proven playbooks to pitch, fundraise, and scale — minus the advisor price tag.",
      details: (
        <>
          <p className='mr-10'>
            Step-by-step playbooks built for real founders in real markets.
          </p>
          <ul className='py-2 pl-4 space-y-0.5 list-disc'>
            <li>How to build a pitch deck that converts</li>
            <li>How to identify the right Investors</li>
            <li>How to structure your fundraising round</li>
            <li>How to negotiate term sheets</li>
            <li>
              How to scale from Seed{" "}
              <ArrowRight className='inline-block size-4' /> Series A{" "}
              <ArrowRight className='inline-block size-4' /> Series B
            </li>
            <li>Cap Tables & Valuation Modelling</li>
            <li>Strategic Planning Tools</li>
          </ul>
          <p className='font-semibold'>
            Everything the best advisors would tell you — without the $250K
            consulting fees.
          </p>
        </>
      ),
    },
    {
      icon: MapPinSimpleArea,
      title: "Deep Market Insights & Trends",
      description:
        "Know the market (and your competitors) better than your competitors.",
      details: (
        <>
          <p className='mr-10'>{`Access the region's most comprehensive data library.`}</p>
          <ul className='py-2 pl-4 space-y-0.5 list-disc'>
            <li>Funding flows across ME&A</li>
            <li>
              Top Startups by Sector (AI, Fintech, Mobility, Gaming, etc.)
            </li>
            <li>Market Size, Policy, and Upcoming Regulations</li>
            <li>Benchmarking and Sector Deep Dives</li>
            <li>Weekly insights & ecosystem dashboards</li>
          </ul>
          <p className='font-semibold'>
            Know the market better than your competitors.
          </p>
        </>
      ),
    },
    {
      icon: Target,
      title: "Investor Intel & Matchmaking",
      description: "Raise Smarter. Not Harder.",
      details: (
        <>
          <p className='mr-10'>
            Find the right investor instantly - based on stage, sector,
            geography, and thesis.
          </p>
          <ul className='py-2 pl-4 space-y-0.5 list-disc'>
            <li>800+ VCs, Angels, and Funds</li>
            <li>Verified startup & investor profiles</li>
            <li>AI-powered matchmaking recommendations</li>
            <li>Warm intros & frictionless connections</li>
            <li>Fundraising Guidance & Courses</li>
          </ul>
          <p className='font-semibold'>Raise smarter. Not harder.</p>
        </>
      ),
    },
    {
      icon: Eyes,
      title: "Startup & Founder Visibility",
      description: "Visibility = Credibility. Credibility = Capital.",
      details: (
        <>
          <p className='mr-10'>Get your Startup Discovered:</p>
          <ul className='py-2 pl-4 space-y-0.5 list-disc'>
            <li>Featured on LucidityInsights.com</li>
            <li>Featured with Publishing Partners</li>
            <li>Highlighted in Reports, Videos & Newsletters</li>
            <li>Included in Deal-flow Channels</li>
            <li>Eligible for Spotlight Series & Events</li>
          </ul>
          <p className='font-semibold'>
            Visibility = credibility. Credibility = capital.
          </p>
        </>
      ),
    },
  ];

  // Determine trial period text based on URL query
  const trialText = router.query.trial === "90" ? "90 Day" : "7 Day";

  // Update currentPriceData when billingCycle changes
  useEffect(() => {
    if (billingCycle === "monthly") {
      setCurrentPriceData(priceDataMonthly);
    } else {
      setCurrentPriceData(priceData);
    }
  }, [billingCycle, priceData, priceDataMonthly]);

  const handlePlayPause = () => {
    setIsPlaying(!playing);
  };

  const onPurchase = () => {
    if (user) {
      // setOpenPurchasePopup("startup");
      setIsOpen(true);
    } else {
      setOpenLoginPopup(true);
      // localStorage.setItem("nextStep", "purchasePopup");
    }
  };

  const handlePurchaseStartup = () => {
    if (user) {
      setOpenPurchaseStartupPopup(true);
    } else {
      setOpenLoginPopup(true);
      localStorage.setItem("nextStep", "purchaseStartupPopup");
    }
  };

  const validateStartupInfo = async (
    startupName,
    startupWebsite,
    userEmail
  ) => {
    // Reset errors
    setFormErrors({});

    // Basic validation
    const errors = {};
    if (!startupName?.trim()) {
      errors.startupName = "Startup name is required!";
    }

    // Validate website if provided
    if (startupWebsite && !isValidDomain(startupWebsite)) {
      errors.startupWebsite =
        "The website is invalid!" +
        (!startupWebsite.includes("http")
          ? " It must have https:// or http:// at the beginning."
          : "");
    }

    // If no email (guest user), require email
    if (!userEmail?.trim()) {
      errors.email = "Email is required!";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return { isValid: false, errors };
    }

    // If no website provided, startup can be registered but not published
    if (!startupWebsite) {
      setIsSameUserEmailAndWebsite(false);
      setStartupExist(false);
      setFoundStartup(null);
      return {
        isValid: true,
        canProceed: true,
        canBePublished: false,
        message:
          "Startup can be registered but cannot be published without a website.",
      };
    }

    // Check if startup already exists
    try {
      const checkStartupResponse = await checkStartupExistNoLogin({
        name: startupName,
        website: startupWebsite,
        email: userEmail,
      });

      setStartupExist(checkStartupResponse.data.exists);

      if (checkStartupResponse.data.exists) {
        setFoundStartup(checkStartupResponse.data.startup);
        const startup = checkStartupResponse.data.startup;

        if (startup.owner_id) {
          return {
            isValid: false,
            errors: {
              general: `A startup named "${startupName}" already exists and has been claimed by another user.`,
            },
          };
        } else {
          return {
            isValid: false,
            errors: {
              general: `A startup named "${startupName}" already exists in our database.`,
            },
          };
        }
      }

      // Check if user email domain matches website domain
      const emailMatches = await checkEmailDomainWithList(
        userEmail,
        startupWebsite
      );
      setIsSameUserEmailAndWebsite(emailMatches);

      return {
        isValid: true,
        canProceed: true,
        canBePublished: emailMatches,
        message: emailMatches
          ? "Startup can be registered and published."
          : "Startup can be registered but cannot be published until website verification is completed.",
      };
    } catch (error) {
      console.error("Error validating startup:", error);
      return {
        isValid: false,
        errors: {
          general:
            "Something went wrong while checking your startup. Please try again in a moment.",
        },
      };
    }
  };

  const handleStartupTrial = (questionStart = "q0") => {
    // Open the direct form without requiring login
    //  setIsDirectFormOpen(true);
    setStartingQuestion(questionStart);
    setIsPurchasePopup(true);
  };

  const handleStartupTrialQ0 = () => {
    handleStartupTrial("q0");
  };

  const handleStartupTrialQ1 = () => {
    handleStartupTrial("q1");
  };

  const handleInvestorTrialQX = () => {
    handleStartupTrial("qx");
  };

  const handleOpenStartupRegistration = (prefilledData) => {
    // Clear coupon data when opening a new plan
    setCoupon(null);
    setInputCoupon("");
    setCouponError(false);
    setCouponErrorProduct(false);
    setIsCoupon(false);

    // Set pre-filled data from the question flow
    setFullNamePayloadForGuest(prefilledData.fullName || "");
    setEmailPayloadForGuest(prefilledData.email || "");
    setPhonePayloadForGuest(prefilledData.phone || "");
    setSelectedPlan("startup");
    // Open the OnUpgradeAtStripeCheckout modal
    setIsDirectFormOpen(true);
  };

  const handleOpenContentAccess = (prefilledData) => {
    // Clear coupon data when opening a new plan
    setCoupon(null);
    setInputCoupon("");
    setCouponError(false);
    setCouponErrorProduct(false);
    setIsCoupon(false);

    // Set pre-filled data from the question flow
    setFullNamePayloadForGuest(prefilledData.fullName || "");
    setEmailPayloadForGuest(prefilledData.email || "");
    setPhonePayloadForGuest(prefilledData.phone || "");
    setSelectedPlan("content");
    // Open the OnUpgradeAtStripeCheckout modal for Content Access
    setIsDirectFormOpen(true);
  };

  const handleStartupTrialOld = () => {
    if (user) {
      // User is logged in, open StartupCheckout modal
      setIsStartupCheckoutOpen(true);
    } else {
      // User is not logged in, open login modal and set next step
      setOpenLoginPopup(true);
      localStorage.setItem("nextStep", "startupCheckout");
    }
  };

  const handleCheckboxChange = () => {
    setIsCoupon(!isCoupon);
    if (isCoupon) {
      setCoupon(null);
      setInputCoupon("");
      setCouponError(false);
      setCouponErrorProduct(false);
    }
  };

  const handleCouponChange = (e) => {
    setInputCoupon(e.target.value);
    setCouponError(false);
    setCouponErrorProduct(false);
  };

  const handleCheckCoupon = async (e) => {
    e.preventDefault();
    if (!inputCoupon.trim()) return;
    if (loadingCheck || coupon) return;

    setCouponError(false);
    setCouponErrorProduct(false);
    setCoupon(null);
    setLoadingCheck(true);

    try {
      const res = await checkCoupon(inputCoupon);

      if (res.data.redeem_by && Date.now() > res.data.redeem_by * 1000) {
        setCouponError(true);
        setLoadingCheck(false);
        return;
      }

      // Check if coupon applies to specific products
      if (res.data.applies_to?.products) {
        const applicableProducts = res.data.applies_to.products;
        const currentProduct =
          selectedPlan === "content"
            ? currentPriceData["Content Access"]?.product
            : currentPriceData["Startup Registration"]?.product;

        if (applicableProducts.includes(currentProduct)) {
          setCoupon(res.data);
        } else {
          setCoupon(null);
          setCouponErrorProduct(true);
        }
      } else {
        // If no product restrictions, coupon applies to all products
        setCoupon(res.data);
      }

      setLoadingCheck(false);
    } catch (error) {
      console.error("Error checking coupon:", error);
      setCouponError(true);
      setLoadingCheck(false);
    }
  };

  const onRemoveCoupon = () => {
    setCoupon(null);
    setInputCoupon("");
    setCouponError(false);
    setCouponErrorProduct(false);
  };

  const handleSubscription = async (options = {}) => {
    if (!currentPriceData) {
      console.error("Price data not available");
      return;
    }

    try {
      const { loadStripe } = await import("@stripe/stripe-js");
      const { createStripeCheckout, createStripeCheckoutForGuest } =
        await import("@/services/api/Stripe");

      const stripePromise = loadStripe(
        process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
      );
      const stripe = await stripePromise;

      if (!stripe) {
        console.error("Stripe failed to initialize");
        return;
      }

      const { successUrl = null, cancelUrl = null } = options;

      // Determine plan name and type based on selectedPlan
      const selectedPlanName =
        selectedPlan === "content"
          ? "Content Access"
          : selectedPlan === "vc"
            ? "VC Registration"
            : selectedPlan === "angel"
              ? "Angel Investor Registration"
              : "Startup Registration";

      const planType =
        selectedPlan === "content"
          ? "content"
          : selectedPlan === "vc"
            ? "vc"
            : selectedPlan === "angel"
              ? "angel"
              : "startup";

      // Check if trial query parameter is exactly 90
      const shouldApply90DayTrial = router.query.trial === "90";

      // Note: Startup creation is handled by OnUpgradeAtStripeCheckout component
      // No need to create startup here again

      let response;

      if (!user) {
        // For guest users, use createStripeCheckoutForGuest
        const payload = {
          toPlanName: selectedPlanName,
          email: emailPayloadForGuest,
          priceId: currentPriceData[selectedPlanName].id,
          type: planType,
          coupon_id: coupon ? coupon.id : null,
          cancel_url: cancelUrl
            ? process.env.NEXT_PUBLIC_BASE_URL + "/" + cancelUrl
            : process.env.NEXT_PUBLIC_BASE_URL + router.asPath,
          success_url:
            process.env.NEXT_PUBLIC_BASE_URL +
            "/" +
            (successUrl ||
              (selectedPlan === "content"
                ? "contentaccess"
                : "startupregistration")),
        };

        // Add trial_period_days only when trial query is exactly 90
        if (shouldApply90DayTrial) {
          payload.trial_period_days = 90;
        }

        response = await createStripeCheckoutForGuest(payload);
      } else {
        // For logged-in users, use createStripeCheckout
        const payload = {
          hasSubscription:
            planSubscription && planSubscription.has_active_subscription,
          subscriptionId: planSubscription?.subscription_detail?.id,
          fromPlanName: planSubscription?.active_product?.name,
          toPlanName: selectedPlanName,
          userEmail: user.email,
          priceId: currentPriceData[selectedPlanName].id,
          type: planType,
          coupon_id: coupon ? coupon.id : null,
          cancel_url: cancelUrl
            ? process.env.NEXT_PUBLIC_BASE_URL + "/" + cancelUrl
            : process.env.NEXT_PUBLIC_BASE_URL + router.asPath,
          success_url:
            process.env.NEXT_PUBLIC_BASE_URL +
            "/" +
            (successUrl ||
              (selectedPlan === "content"
                ? "contentaccess"
                : "startupregistration")),
        };

        // Add trial_period_days only when trial query is exactly 90
        if (shouldApply90DayTrial) {
          payload.trial_period_days = 90;
        }

        response = await createStripeCheckout(payload);
      }

      const session = response.data;

      if (session.id) {
        // Redirect to Stripe checkout
        const result = await stripe.redirectToCheckout({
          sessionId: session.id,
        });

        if (result.error) {
          console.error("Stripe checkout error:", result.error.message);
          alert(result.error.message);
        }
      } else {
        console.error("No session ID returned from Stripe");
        alert("Failed to create payment session. Please try again.");
      }
    } catch (error) {
      console.error("Error creating subscription:", error);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert("Failed to start payment process. Please try again.");
      }
    }
  };

  const onCancel = () => {
    setIsDirectFormOpen(false);
  };

  const handleUnmute = () => {
    setIsMuted(false);
  };

  const fetchFeaturedStartups = async () => {
    setIsSearchingStartups(true);
    try {
      const res = await getStartupList({
        params: {
          highlighted: true,
          per_page: 12,
        },
      });
      setFeaturedStartups(res.data.data);
    } catch (error) {
      console.error("Error fetching featured startups:", error);
      setFeaturedStartups([]);
    }
    setIsSearchingStartups(false);
  };

  const handleStartupSearch = async (e) => {
    e.preventDefault();
    if (!startupKeyword.trim()) return;

    setIsSearchingStartups(true);
    try {
      const res = await searchStartup({
        params: {
          keyword: startupKeyword,
          per_page: 8,
          sort: "amount",
        },
      });
      setFeaturedStartups(res.data.data);
    } catch (error) {
      console.error("Error searching startups:", error);
      setFeaturedStartups([]);
    } finally {
      setIsSearchingStartups(false);
    }
  };

  const resetStartupSearch = async () => {
    setStartupKeyword("");
    await fetchFeaturedStartups();
  };

  const handleInvestorSearch = async (e) => {
    e?.preventDefault();
    setIsSearchingInvestors(true);

    try {
      const res = await searchInvestor({
        params: {
          keyword: investorKeyword || "*",
          per_page: 8,
          claimed: !investorKeyword,
          is_angel: false,
        },
      });
      setInvestors(res.data.data);
    } catch (error) {
      console.error("Error searching investors:", error);
      setInvestors([]);
    } finally {
      setIsSearchingInvestors(false);
    }
  };

  const resetInvestorSearch = async () => {
    setInvestorKeyword("");
    setIsSearchingInvestors(true);
    try {
      const res = await searchInvestor({
        params: {
          keyword: "*",
          per_page: 8,
          claimed: true,
          is_angel: false,
        },
      });
      setInvestors(res.data.data);
    } catch (error) {
      console.error("Error fetching investors:", error);
      setInvestors([]);
    } finally {
      setIsSearchingInvestors(false);
    }
  };

  const getUserWishlist = async () => {
    // Simple placeholder for wishlist functionality
    setUserWishlist([]);
    setUserWishlistIds([]);
  };

  const getWishlistCategories = async () => {
    // Simple placeholder for wishlist categories
    setWishlistCategories(null);
  };

  // Memoize investor grid to prevent re-renders when keyword changes
  const investorGrid = useMemo(() => {
    return investors.map((investor, i) => (
      <InvestorCard
        investor={investor}
        key={`investor-${investor.id || i}`}
        claimable={!investor.owner_id}
        user={user}
        newTab={true}
      />
    ));
  }, [investors, user]);

  useEffect(() => {
    fetchFeaturedStartups();
    fetchInitialInvestors();
  }, []);

  const fetchInitialInvestors = async () => {
    try {
      const res = await searchInvestor({
        params: {
          keyword: "",
          per_page: 8,
          claimed: true,
          is_angel: false,
        },
      });
      setInvestors(res.data.data);
    } catch (error) {
      console.error("Error fetching investors:", error);
    }
  };

  const trusted_logos = [
    { src: logo1, height: "h-9 lg:h-12" },
    { src: logo2, height: "h-9 lg:h-10" },
    { src: logo3, height: "h-9 lg:h-12" },
    { src: logo4, height: "h-7 lg:h-10" },
    { src: logo5, height: "h-7 lg:h-10" },
  ];

  const [isPlayingPodcast, setIsPlayingPodcast] = useState(false);
  const [isPlaying1, setIsPlaying1] = useState(true);
  const [isPlaying2, setIsPlaying2] = useState(false);
  const [isMuted1, setIsMuted1] = useState(true);

  return (
    <Layout
      title='Connect with Investors - Startup Registration for ME&A Founders | Lucidity Insights'
      description='Research & Find the Right Investors for Your Startup. Publish your startup profile and submit your pitch deck directly to active VCs and Angel Investors.'
    >
      <section className='relative'>
        <span className='absolute top-0 left-0 w-full h-full lg:w-1/2 bg-gradient-to-tr from-orange-500 to-purple-600'></span>
        <div className='absolute top-0 hidden w-1/2 h-full lg:flex left-1/2'>
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className='flex-1 h-full bg-gradient-to-tr from-background to-purple-500/20 lg:to-purple-500/10 backdrop-blur-2xl'
            ></div>
          ))}
        </div>
        <div className='container relative grid h-full gap-8 py-10 lg:gap-0 lg:py-16 lg:grid-cols-2 '>
          <div className='relative flex flex-col justify-center h-full lg:pr-12'>
            <h1 className='text-3xl font-bold leading-none tracking-tighter sm:text-4xl md:text-5xl xl:text-6xl'>
              Build. Fund. Scale. <br /> All From One Platform.
            </h1>
            <p className='mt-3 text-sm leading-relaxed lg:mt-4 opacity-80'>
              {`Lucidity Insights is the region’s first all-in-one Founder Hub — unifying data-rich investor and startup profiles, smart investor matchmaking, deep market intelligence across ME&A’s hottest tech sectors, and step-by-step fundraising and scaling tools and guidance. Whether you're raising your first round or preparing for a Series C, Lucidity equips founders with everything they need to build smarter, fund faster, and scale confidently across the Middle East and Africa — all in one platform.`}
            </p>
            <div className='grid grid-cols-2 gap-2 mt-5 font-medium sm:grid-cols-4 lg:mt-8 lg:text-sm'>
              <p className='flex flex-col justify-between gap-2 xl:gap-6 px-3 xl:px-4 py-2 xl:py-2.5 bg-white rounded-lg text-background'>
                <span>
                  <Person
                    weight='light'
                    className='text-orange-500 size-8 xl:size-9 mb-1.5'
                  />
                  <span className='block text-xs leading-snug opacity-70'>
                    Pre-Qualified Investors
                  </span>
                </span>
                <span className='text-3xl font-bold tracking-tight md:text-2xl xl:text-4xl'>
                  {seoData?.additional_data?.venture_capital_count?.toLocaleString() ||
                    "-"}
                  +
                </span>
              </p>
              <p className='flex flex-col justify-between gap-2 xl:gap-6 px-3 xl:px-4 py-2 xl:py-2.5 bg-white rounded-lg text-background'>
                <span>
                  <RocketLaunch
                    weight='light'
                    className='text-orange-500 size-8 xl:size-9 mb-1.5'
                  />
                  <span className='block text-xs leading-snug opacity-70'>
                    Startups in the Region
                  </span>
                </span>
                <span className='text-3xl font-bold tracking-tight md:text-2xl xl:text-4xl'>
                  {seoData?.additional_data?.startup_count?.toLocaleString() ||
                    "-"}
                  +
                </span>
              </p>
              <p className='flex flex-col justify-between gap-2 xl:gap-6 px-3 xl:px-4 py-2 xl:py-2.5 bg-white rounded-lg text-background'>
                <span>
                  <GlobeHemisphereEast
                    weight='light'
                    className='text-orange-500 size-8 xl:size-9 mb-1.5'
                  />
                  <span className='block text-xs leading-snug opacity-70'>
                    Countries Across The Globe
                  </span>
                </span>
                <span className='text-3xl font-bold tracking-tight md:text-2xl xl:text-4xl'>
                  {seoData?.additional_data?.country_count?.toLocaleString() ||
                    "-"}
                  +
                </span>
              </p>
              <p className='flex flex-col justify-between gap-2 xl:gap-6 px-3 xl:px-4 py-2 xl:py-2.5 bg-white rounded-lg text-background'>
                <span>
                  <Brain
                    weight='light'
                    className='text-orange-500 size-8 xl:size-9 mb-1.5'
                  />
                  <span className='block text-xs leading-snug opacity-70'>
                    Smart Matching Platform
                  </span>
                </span>
                <span className='text-3xl font-bold tracking-tight md:text-2xl xl:text-4xl'>
                  1
                </span>
              </p>
            </div>

            <div className='flex flex-col gap-2 mt-5 lg:mt-8 lg:flex-row'>
              <Button
                onClick={handleStartupTrialQ0}
                className='px-7 py-3 text-sm rounded-lg !h-auto text-white bg-background hover:bg-background/60'
              >
                Start {trialText} Free Trial
              </Button>
            </div>
          </div>
          <div className='relative flex flex-col justify-center order-first lg:order-last'>
            <span className='absolute left-1/2 top-1/2 blur-3xl -translate-x-1/2 -translate-y-1/2 size-[320px] bg-gradient-to-tr from-orange-500 to-purple-600'></span>
            <div className='relative lg:ml-12'>
              <p className='mb-2 text-lg font-medium text-center lg:mb-4 lg:text-2xl font-display'>
                What is Lucidity Insights?{" "}
              </p>
              <div className='relative'>
                {isMuted1 && (
                  <button
                    onClick={() => setIsMuted1(false)}
                    className='absolute z-10 inline-flex items-center justify-center font-medium -translate-x-1/2 -translate-y-1/2 rounded-full size-12 top-1/2 left-1/2 lg:size-16 border-background bg-gradient-to-tr from-yellow-1 to-red-1 pulse'
                  >
                    <SpeakerHigh
                      weight='fill'
                      className='flex-shrink-0 text-2xl'
                    />
                  </button>
                )}
                <div className='relative lg:p-1.5 bg-gradient-to-tr from-orange-500 to-purple-500 rounded-2xl'>
                  <ReactPlayer
                    width='100%'
                    height='100%'
                    url={
                      "https://cdn.lucidityinsights.com/uploads/lucidity/adv2025.mp4"
                    }
                    playing={isPlaying1}
                    muted={isMuted1}
                    loop={true}
                    controls={isPlaying1}
                    className='overflow-hidden rounded-2xl'
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className='my-10 lg:my-20'>
          <p className='mb-5 text-base font-medium text-center lg:text-lg lg:mb-8'>
            As Seen On
          </p>
          <div className='flex flex-wrap justify-center gap-4 lg:gap-12 lg:flex-nowrap'>
            {trusted_logos.map((logo, index) => (
              <Image
                key={index}
                src={logo.src}
                alt='Slider Image'
                className={`w-auto ${logo.height ?? "h-6 lg:h-7"}`}
                width={85}
                height={50}
              />
            ))}
          </div>
        </div>
      </section>

      <section className='bg-white/5'>
        <div className='container flex items-center gap-12'>
          <div className='hidden lg:block relative w-1/2 aspect-[4/3]'>
            <Image
              src={tired.src}
              alt='Slider Image'
              layout={"fill"}
              objectFit={"cover"}
            />
          </div>
          <div className='py-10 lg:w-1/2 lg:py-16'>
            <h2 className='text-3xl font-semibold leading-none tracking-tighter sm:text-4xl lg:text-5xl'>
              Support is limited for Fundraising & Building Startups in ME&A
            </h2>
            <p className='mt-4 text-sm leading-relaxed opacity-60'>
              If you’re a startup founder, you already know the pain-points:
            </p>
            <div className='grid grid-cols-1 gap-3 mt-5 text-base font-medium leading-snug tracking-tight text-left lg:text-lg'>
              <div className='flex items-start gap-3'>
                <span className='inline-flex items-center justify-center text-lg bg-red-500 rounded-full shrink-0 size-6 lg:size-7'>
                  <X weight='bold' />
                </span>
                Hard to find the right investor for your stage & sector
              </div>
              <div className='flex items-start gap-3'>
                <span className='inline-flex items-center justify-center text-lg bg-red-500 rounded-full shrink-0 size-6 lg:size-7'>
                  <X weight='bold' />
                </span>
                No central, trustworthy database of regional investors &
                startups
              </div>
              <div className='flex items-start gap-3'>
                <span className='inline-flex items-center justify-center text-lg bg-red-500 rounded-full shrink-0 size-6 lg:size-7'>
                  <X weight='bold' />
                </span>
                Advisors and Consultants are too expensive for Startups to
                access
              </div>
              <div className='flex items-start gap-3'>
                <span className='inline-flex items-center justify-center text-lg bg-red-500 rounded-full shrink-0 size-6 lg:size-7'>
                  <X weight='bold' />
                </span>
                Fragmented tech market insights across 70+ ME&A countries
              </div>
              <div className='flex items-start gap-3'>
                <span className='inline-flex items-center justify-center text-lg bg-red-500 rounded-full shrink-0 size-6 lg:size-7'>
                  <X weight='bold' />
                </span>
                Startups struggle to gain visibility in the market
              </div>
            </div>

            <p className='mt-6 text-sm'>
              {`Founders don't lack ambition - they lack a unified platform built
              for them. Get the tools, insights and advice only investors had
              access to before.`}
            </p>
          </div>
        </div>
      </section>
      <section className='bg-white/5'>
        <div className='container flex items-center gap-12'>
          <div className='py-10 lg:w-1/2 lg:py-16'>
            <h2 className='text-3xl font-semibold leading-none tracking-tighter sm:text-4xl lg:text-5xl'>
              Lucidity Insights bring everything founders need - into one
              connected ecosystem.
            </h2>
            <p className='mt-4 text-sm leading-relaxed opacity-60'>
              Instead of jumping between 12 tools, accelerators, reports and
              cold emails, Lucidity gives you:
            </p>
            <div className='grid grid-cols-1 gap-3 mt-5 text-base font-medium leading-snug tracking-tight text-left lg:text-lg'>
              <div className='flex items-start gap-3'>
                <span className='inline-flex items-center justify-center text-lg bg-green-500 rounded-full shrink-0 size-6 lg:size-7'>
                  <Check weight='bold' />
                </span>
                A single platform
              </div>
              <div className='flex items-start gap-3'>
                <span className='inline-flex items-center justify-center text-lg bg-green-500 rounded-full shrink-0 size-6 lg:size-7'>
                  <Check weight='bold' />
                </span>
                Built for founders
              </div>
              <div className='flex items-start gap-3'>
                <span className='inline-flex items-center justify-center text-lg bg-green-500 rounded-full shrink-0 size-6 lg:size-7'>
                  <Check weight='bold' />
                </span>
                Backed by real data
              </div>
              <div className='flex items-start gap-3'>
                <span className='inline-flex items-center justify-center text-lg bg-green-500 rounded-full shrink-0 size-6 lg:size-7'>
                  <Check weight='bold' />
                </span>
                Designed for the ME&A innovation economy
              </div>
            </div>

            <p className='mt-6 text-sm'>
              This is the AI-powered Advisor-as-a-Service model - finally
              accessible to every founder.
            </p>
          </div>
          <div className='hidden lg:block relative w-1/2 aspect-[4/3]'>
            <Image
              src={ai_platform.src}
              alt='Slider Image'
              layout={"fill"}
              objectFit={"cover"}
            />
          </div>
        </div>
      </section>
      <section className='bg-gradient-to-bl from-purple-1 via-red-1 to-yellow-1'>
        <div className='container'>
          <div className='py-10 lg:py-16'>
            <h2 className='text-3xl font-semibold leading-none tracking-tighter text-center sm:text-4xl lg:text-5xl'>
              The Lucidity Platform has 4 Powerful Pillars
            </h2>

            <div className='grid gap-5 mt-8 font-medium transition-colors sm:gap-3 md:gap-0 md:-mx-4 sm:grid-cols-4 lg:mt-8 lg:text-sm'>
              {features.map((item, i) => (
                <FeatureItem key={i} {...item} />
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className=''>
        <div className='container flex flex-wrap items-center gap-16 py-12 lg:flex-nowrap lg:py-20'>
          <div className='lg:w-1/2'>
            <h2 className='text-3xl font-semibold leading-none tracking-tighter sm:text-4xl lg:text-5xl'>
              {`The Region's First  Founder-Investor Matchmaking Platform`}
            </h2>
            <p className='mt-4 text-sm leading-relaxed opacity-60'>
              Think Tinder, but, for deal flow (and less hanky panky).
            </p>
            <p className='mt-4 text-sm leading-relaxed opacity-60'>
              Our smart matching algorithm helps startup founders filter and
              identify investors based on:
            </p>

            <div className='grid lg:grid-cols-6 lg:gap-1.5 mt-4 font-medium gap-3'>
              <div className='inline-flex items-center gap-2 rounded-lg lg:p-3 lg:col-span-2 lg:bg-white/10'>
                <ChartPieSlice
                  weight='bold'
                  className='text-orange-500 size-6'
                />
                Sector Fit
              </div>
              <div className='inline-flex items-center gap-2 rounded-lg lg:p-3 lg:col-span-2 lg:bg-white/10'>
                <ChartBar weight='bold' className='text-orange-500 size-6' />
                Funding Stage
              </div>
              <div className='inline-flex items-center gap-2 rounded-lg lg:p-3 lg:col-span-2 lg:bg-white/10'>
                <GlobeHemisphereWest
                  weight='bold'
                  className='text-orange-500 size-6'
                />
                Geography
              </div>
              <div className='inline-flex items-center gap-2 rounded-lg lg:p-3 lg:col-span-3 lg:bg-white/10'>
                <HandCoins weight='bold' className='text-orange-500 size-6' />
                Investment Thesis Alignment
              </div>
              <div className='inline-flex items-center gap-2 rounded-lg lg:p-3 lg:col-span-3 lg:bg-white/10'>
                <ChartScatter
                  weight='bold'
                  className='text-orange-500 size-6'
                />
                Real-time Investment Activity
              </div>
            </div>

            <p className='py-3 pl-4 mt-4 text-sm border-l-2 border-orange-500'>
              <span className='inline-block border-b-2 border-orange-500 border-dashed'>
                No guesswork
              </span>
              .{" "}
              <span className='inline-block border-b-2 border-orange-500 border-dashed'>
                No gatekeepers
              </span>
              . Just meaningful matches, that amplify your chances of getting
              your pitch deck in front of the RIGHT investors.
            </p>

            <div className='flex flex-col gap-2 mt-4 lg:flex-row'>
              <Button
                onClick={handleStartupTrialQ1}
                className='px-7 py-3 text-sm rounded-lg !h-auto text-white bg-gradient-to-l from-orange-1 to-red-1'
              >
                Create my Startup Profile
              </Button>
              <Button
                asChild
                className='px-7 py-3 text-sm rounded-lg !h-auto text-white bg-transparent border border-white/30 hover:bg-white/10'
              >
                <a
                  href={FUNDRAISING_READINESS_TEST_URL}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Take Fundraising Readiness Test
                </a>
              </Button>
            </div>
          </div>
          <div className='relative hidden lg:block lg:w-1/2'>
            <span className='w-[550px] h-[250px] left-1/2 -translate-x-1/2 top-1/2 blur-3xl opacity-80 -translate-y-1/2 absolute rounded-full bg-gradient-to-tr from-yellow-1 to-red-1'></span>
            <Image
              src={startupMatch.src}
              alt='Slider Image'
              width={1000}
              height={1000}
              className='relative w-full'
            />
          </div>
        </div>
      </section>

      <section className='max-w-5xl px-4 pb-12 mx-auto lg:pb-20'>
        <FundraisingReadinessBanner className='sm:aspect-[3/1]' />
      </section>

      <section className='max-w-5xl px-4 mx-auto'>
        <div className='relative w-full p-2 py-6 overflow-hidden text-white lg:px-8 lg:py-10 bg-gradient-to-tr from-purple-1 via-red-1 to-yellow-1 rounded-2xl'>
          <svg
            className='absolute bottom-0 object-cover rotate-[163deg] translate-x-10 opacity-50 scale-150'
            viewBox='0 0 723 542'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <g clipPath='url(#clip0_3829_2406)'>
              <mask
                id='mask0_3829_2406'
                style={{ maskType: "alpha" }}
                maskUnits='userSpaceOnUse'
                x='0'
                y='-181'
                width='723'
                height='723'
              >
                <rect y='-181' width='723' height='723' fill='#D9D9D9' />
              </mask>
              <g mask='url(#mask0_3829_2406)'>
                <g filter='url(#filter0_d_3829_2406)'>
                  <circle
                    cx='178.196'
                    cy='-61.5215'
                    r='291.5'
                    transform='rotate(-120 178.196 -61.5215)'
                    fill='url(#paint0_linear_3829_2406)'
                  />
                </g>
                <g filter='url(#filter1_d_3829_2406)'>
                  <circle
                    cx='192.837'
                    cy='-116.163'
                    r='291.5'
                    transform='rotate(-120 192.837 -116.163)'
                    fill='url(#paint1_linear_3829_2406)'
                  />
                </g>
                <g filter='url(#filter2_d_3829_2406)'>
                  <circle
                    cx='216.139'
                    cy='-175.804'
                    r='291.5'
                    transform='rotate(-120 216.139 -175.804)'
                    fill='url(#paint2_linear_3829_2406)'
                  />
                </g>
              </g>
            </g>
            <defs>
              <filter
                id='filter0_d_3829_2406'
                x='-133.352'
                y='-371.07'
                width='623.097'
                height='623.097'
                filterUnits='userSpaceOnUse'
                colorInterpolationFilters='sRGB'
              >
                <feFlood floodOpacity='0' result='BackgroundImageFix' />
                <feColorMatrix
                  in='SourceAlpha'
                  type='matrix'
                  values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                  result='hardAlpha'
                />
                <feOffset dy='2' />
                <feGaussianBlur stdDeviation='10' />
                <feComposite in2='hardAlpha' operator='out' />
                <feColorMatrix
                  type='matrix'
                  values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0'
                />
                <feBlend
                  mode='normal'
                  in2='BackgroundImageFix'
                  result='effect1_dropShadow_3829_2406'
                />
                <feBlend
                  mode='normal'
                  in='SourceGraphic'
                  in2='effect1_dropShadow_3829_2406'
                  result='shape'
                />
              </filter>
              <filter
                id='filter1_d_3829_2406'
                x='-118.711'
                y='-425.711'
                width='623.097'
                height='623.097'
                filterUnits='userSpaceOnUse'
                colorInterpolationFilters='sRGB'
              >
                <feFlood floodOpacity='0' result='BackgroundImageFix' />
                <feColorMatrix
                  in='SourceAlpha'
                  type='matrix'
                  values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                  result='hardAlpha'
                />
                <feOffset dy='2' />
                <feGaussianBlur stdDeviation='10' />
                <feComposite in2='hardAlpha' operator='out' />
                <feColorMatrix
                  type='matrix'
                  values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0'
                />
                <feBlend
                  mode='normal'
                  in2='BackgroundImageFix'
                  result='effect1_dropShadow_3829_2406'
                />
                <feBlend
                  mode='normal'
                  in='SourceGraphic'
                  in2='effect1_dropShadow_3829_2406'
                  result='shape'
                />
              </filter>
              <filter
                id='filter2_d_3829_2406'
                x='-95.4097'
                y='-485.352'
                width='623.097'
                height='623.097'
                filterUnits='userSpaceOnUse'
                colorInterpolationFilters='sRGB'
              >
                <feFlood floodOpacity='0' result='BackgroundImageFix' />
                <feColorMatrix
                  in='SourceAlpha'
                  type='matrix'
                  values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                  result='hardAlpha'
                />
                <feOffset dy='2' />
                <feGaussianBlur stdDeviation='10' />
                <feComposite in2='hardAlpha' operator='out' />
                <feColorMatrix
                  type='matrix'
                  values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0'
                />
                <feBlend
                  mode='normal'
                  in2='BackgroundImageFix'
                  result='effect1_dropShadow_3829_2406'
                />
                <feBlend
                  mode='normal'
                  in='SourceGraphic'
                  in2='effect1_dropShadow_3829_2406'
                  result='shape'
                />
              </filter>
              <linearGradient
                id='paint0_linear_3829_2406'
                x1='232.718'
                y1='-312.767'
                x2='-113.304'
                y2='-312.767'
                gradientUnits='userSpaceOnUse'
              >
                <stop stopColor='#e14f4f' />
                <stop offset='0.800681' stopColor='#E14F4F' />
                <stop offset='1' stopColor='#fdaf3a' />
              </linearGradient>
              <linearGradient
                id='paint1_linear_3829_2406'
                x1='247.36'
                y1='-367.408'
                x2='-98.6625'
                y2='-367.408'
                gradientUnits='userSpaceOnUse'
              >
                <stop stopColor='#e14f4f' />
                <stop offset='0.800681' stopColor='#E14F4F' />
                <stop offset='1' stopColor='#fdaf3a' />
              </linearGradient>
              <linearGradient
                id='paint2_linear_3829_2406'
                x1='270.661'
                y1='-427.049'
                x2='-75.3612'
                y2='-427.049'
                gradientUnits='userSpaceOnUse'
              >
                <stop stopColor='#e14f4f' />
                <stop offset='0.800681' stopColor='#E14F4F' />
                <stop offset='1' stopColor='#fdaf3a' />
              </linearGradient>
              <clipPath id='clip0_3829_2406'>
                <rect width='723' height='542' fill='white' />
              </clipPath>
            </defs>
          </svg>
          <Image
            src={backgroundLogo.src}
            alt='Slider Image'
            layout={"fill"}
            objectFit={"cover"}
          />
          <div className='relative flex flex-col items-center text-center'>
            <p className='text-2xl font-semibold leading-tight tracking-tight capitalize lg:text-4xl font-display'>
              Match your Startup with the Right Investors
            </p>
            <p className='max-w-2xl mx-auto mt-2 text-sm lg:mt-3 lg:text-base'>
              Data-backed investor matchmaking built for growth, designed for
              founders & investors in the Middle East & Africa who mean
              business. Build. Match. Fund. Scale.
            </p>
            <Button
              onClick={handleStartupTrialQ1}
              className='mt-3 lg:mt-5 px-7 py-3 text-sm rounded-lg !h-auto bg-white text-black'
            >
              Register your Startup Now
            </Button>
          </div>
        </div>
      </section>

      <section>
        <div className='max-w-5xl px-4 py-12 mx-auto lg:py-20'>
          <h2 className='text-3xl font-semibold tracking-tight text-center lg:text-5xl font-display'>
            How it Works...
            <br />
            Simple. Smart. Strategic.
          </h2>
          <div className='max-w-2xl px-4 py-2 mx-auto mt-6 -my-4 text-sm divide-y rounded-lg lg:px-6 bg-white/5 divide-white/10'>
            <div className='flex items-start justify-between gap-4 py-4 lg:gap-12'>
              <div className='w-20 text-xl font-semibold'>Step 1</div>
              <div className='flex-1'>
                Register your Startup profile in minutes, by starting your free{" "}
                {trialText.toLowerCase()} trial <br /> (Hint: use your startup
                email for professionalism when communicating with investors)
              </div>
            </div>
            <div className='flex items-start justify-between gap-4 py-4 lg:gap-12'>
              <div className='w-20 text-xl font-semibold'>Step 2</div>
              <div className='flex-1'>
                Get verified by Lucidity Insights’ research team. We ensure your
                data is accurate and investor ready
              </div>
            </div>
            <div className='flex items-start justify-between gap-4 py-4 lg:gap-12'>
              <div className='w-20 text-xl font-semibold'>Step 3</div>
              <div className='flex-1'>
                Make sure you Publish your Startup Profile, so you can get
                discovered by active investors searching for startups they may
                be interested in.
              </div>
            </div>
            <div className='flex items-start justify-between gap-4 py-4 lg:gap-12'>
              <div className='w-20 text-xl font-semibold'>Step 4</div>
              <div className='flex-1'>
                Start exploring our investor database that helps you filter for
                investors that fund startups in your sector and your growth
                stage
              </div>
            </div>
            <div className='flex items-start justify-between gap-4 py-4 lg:gap-12'>
              <div className='w-20 text-xl font-semibold'>Step 5</div>
              <div className='flex-1'>
                Save Investors that match your ideal criteria on your{" "}
                <strong>Watchlists</strong>
              </div>
            </div>
            <div className='flex items-start justify-between gap-4 py-4 lg:gap-12'>
              <div className='w-20 text-xl font-semibold'>Step 6</div>
              <div className='flex-1'>
                Send your pitch deck directly to Investors in your Watchlist,
                and wait to hear back from them to book a pitch or meeting.
              </div>
            </div>
            <div className='flex items-start justify-between gap-4 py-4 lg:gap-12'>
              <div className='w-20 text-xl font-semibold'>Step 7</div>
              <div className='flex-1'>
                Keep track of all of the investors you’ve sent a pitch deck to
                by going to ‘My Startup’ in the top right icon bar.
              </div>
            </div>
            <div className='flex items-start justify-between gap-4 py-4 lg:gap-12'>
              <div className='w-20 text-xl font-semibold'>Step 8</div>
              <div className='flex-1'>
                Manage other members of your Startup Team that you want to give
                access to your Startup Profile, to keep it up to date.
              </div>
            </div>
          </div>
          <div className='flex justify-center mt-10'>
            <Link
              href='https://calendly.com/lucy-viqk'
              target='_blank'
              className='inline-flex items-center text-white justify-center px-7 py-3 text-sm rounded-lg !h-auto font-medium bg-gradient-to-l from-red-500 to-purple-600'
            >
              <CalendarCheck weight='bold' className='mr-2' />
              Book a free demo with us
            </Link>
          </div>
        </div>
      </section>

      <section className='text-white bg-gradient-to-tr from-purple-1 via-red-1 to-yellow-1'>
        <div className='container flex flex-wrap items-center gap-8 py-12 lg:gap-16 lg:flex-nowrap lg:py-20'>
          <div className='lg:w-3/5'>
            <h2 className='text-3xl font-semibold leading-none tracking-tight lg:text-5xl font-display'>
              Because Fundraising Shouldn’t Feel Like Shouting into the Void…
            </h2>
            <p className='py-2 pl-4 mt-6 text-base font-medium leading-tight border-l-2 border-orange-500 lg:text-xl'>
              We designed Lucidity Insights for Visibility, Credibility &
              Opportunity
            </p>

            <div className='grid gap-5 mt-4 font-medium md:divide-x sm:gap-3 md:gap-0 md:-mx-4 divide-white/10 sm:grid-cols-3 lg:mt-6 lg:text-sm'>
              <div className='md:px-4'>
                <div className='flex flex-col justify-between gap-2 xl:gap-6 px-3 xl:px-4 py-2 xl:py-2.5 bg-white/15 rounded-lg text-white'>
                  <span className='text-center'>
                    <Sparkle
                      weight='duotone'
                      className=' size-8 mb-1.5 mx-auto'
                    />
                    <span className='text-xl font-bold tracking-tight xl:text-2xl'>
                      Get Featured
                    </span>
                  </span>
                </div>
                <p className='block mt-3 text-xs font-normal leading-relaxed opacity-80'>
                  Verified Startups are featured across Lucidity Insights
                  Reports & Media. We are strategic partners with Entrepreneur
                  Media, where major publications are broadcast globally, to an
                  audience of millions, not just hundreds of thousands
                </p>
              </div>
              <div className='md:px-4'>
                <div className='flex flex-col justify-between gap-2 xl:gap-6 px-3 xl:px-4 py-2 xl:py-2.5 bg-white/15 rounded-lg text-white'>
                  <span className='text-center'>
                    <MagnifyingGlassMinus
                      weight='duotone'
                      className=' size-8 mb-1.5 mx-auto'
                    />
                    <span className='text-xl font-bold tracking-tight xl:text-2xl'>
                      Get Discovered
                    </span>
                  </span>
                </div>
                <p className='block mt-3 text-xs font-normal leading-relaxed opacity-80'>
                  Our platform showcases 477+ that are actively investing in the
                  startups in the Middle East, Africa, Pakistan and Turkey
                  region. More than 70% of them have plugged into the platform,
                  receiving your pitch decks immediately.
                </p>
              </div>
              <div className='md:px-4'>
                <div className='flex flex-col justify-between gap-2 xl:gap-6 px-3 xl:px-4 py-2 xl:py-2.5 bg-white/15 rounded-lg text-white'>
                  <span className='text-center'>
                    <HandCoins
                      weight='duotone'
                      className=' size-8 mb-1.5 mx-auto'
                    />
                    <span className='text-xl font-bold tracking-tight xl:text-2xl'>
                      Get Funded
                    </span>
                  </span>
                </div>
                <p className='block mt-3 text-xs font-normal leading-relaxed opacity-80'>
                  Founders have connected with investors who actually understand
                  their business, and are ready to not just capitalize it, but
                  to mentor and support
                </p>
              </div>
            </div>

            <div className='flex flex-col gap-2 mt-6 sm:flex-row'>
              <Link
                href='https://calendly.com/lucy-viqk'
                target='_blank'
                className='font-medium inline-flex justify-center items-center w-full md:w-auto px-7 py-3 text-sm rounded-lg !h-auto text-white bg-background'
              >
                <CalendarCheck weight='bold' className='mr-2' />
                Book a free demo with us
              </Link>
              <Link
                href='https://forms.gle/djJFoYZ7pUtoewNy6'
                target='_blank'
                className='font-medium inline-flex justify-center items-center w-full md:w-auto px-7 py-3 text-sm rounded-lg !h-auto text-background bg-white'
              >
                <Rocket weight='bold' className='mr-2' />
                Feature my Startup Story
              </Link>
            </div>
          </div>
          <div className='relative lg:flex-1'>
            <p className='pt-5 pb-5 pl-4 text-2xl font-medium tracking-tight border-b-2 border-l-2 border-orange-500 lg:pb-8 lg:pl-8 lg:text-4xl font-display rounded-bl-2xl'>
              “Lucidity Insights helped us get noticed by regional VCs we’d been
              trying to reach for months. Within 1 month, we had several
              investor calls lined up.”
            </p>
            <p className='pl-4 mt-4 text-sm italic lg:pl-8 opacity-60'>
              Founder, Saudi Fintech Startup in Stealth Mode
            </p>
          </div>
        </div>
      </section>

      <section>
        <div className='container py-10 lg:py-20'>
          <div className='max-w-5xl mx-auto'>
            <h2 className='text-3xl font-semibold tracking-tight text-center sm:text-4xl lg:text-5xl font-display'>
              Trusted by both First-Time Founders and Seasoned Entrepreneurs...
            </h2>
            <p className='mt-3 text-sm leading-relaxed text-center opacity-60'>
              {`Thousands of founders across the Middle East & Africa rely on
              Lucidity Insights to grow -- now it's your turn!`}
            </p>

            <div className='flex justify-center mt-5'>
              <button
                type='button'
                onClick={handleStartupTrialQ1}
                className='inline-flex items-center text-white justify-center px-7 py-3 text-sm rounded-lg !h-auto font-medium bg-gradient-to-l from-orange-1 to-red-1'
              >
                Create My Startup Profile
              </button>
            </div>
          </div>
          <div className='grid mt-16 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-12'>
            {featuredStartups.map((startup, i) => (
              <StartupCard
                key={i}
                startup={startup}
                user={user}
                newTab={true}
              />
            ))}
          </div>
        </div>
      </section>

      <section className='py-12 bg-white/5 lg:py-20'>
        <div className='container flex flex-col items-center gap-8 sm:flex-row lg:gap-16 '>
          <div className='w-full sm:flex-1'>
            <h2 className='text-3xl font-semibold leading-none tracking-tight lg:text-5xl font-display'>
              Why Investors Love Us
            </h2>
            <p className='py-2 pl-4 mt-4 text-xl font-medium leading-snug border-l-2 border-orange-500'>
              Because Deal Flow should be Smart and Filtered.
            </p>

            <p className='mt-4 text-sm opacity-60'>
              Lucidity Insights helps investors discover startups that are:
            </p>

            <div className='mt-6 space-y-2.5 text-sm'>
              <p className='flex items-start gap-2'>
                <CheckCircle
                  weight='fill'
                  className='text-green-500 shrink-0 size-5'
                />
                <span>
                  Pre-verified (Startups must meet minimum criteria to pitch to
                  each investor)
                </span>
              </p>
              <p className='flex items-start gap-2'>
                <CheckCircle
                  weight='fill'
                  className='text-green-500 shrink-0 size-5'
                />
                <span>
                  Aligned with their investment thesis (they get to filter out
                  pitches they don’t want)
                </span>
              </p>
              <p className='flex items-start gap-2'>
                <CheckCircle
                  weight='fill'
                  className='text-green-500 shrink-0 size-5'
                />
                <span>Actively raising</span>
              </p>
            </div>
          </div>
          <div className='relative w-full sm:w-[45%] md:w-1/2'>
            <div className='flex flex-col gap-2 text-xl font-normal md:flex-row'>
              <div className='flex items-start gap-3 p-3 bg-white md:block md:gap-0 md:p-5 rounded-xl text-background md:text-center'>
                <FolderLock
                  weight='duotone'
                  className='text-orange-600 md:mx-auto md:mb-2 size-8 xl:size-12'
                />
                <p className='text-base md:text-lg font-semibold leading-tight tracking-tight translate-y-1.5 md:translate-y-0'>
                  Keeps Investor Contacts Private
                </p>
              </div>
              <div className='flex items-start gap-3 p-3 bg-white md:block md:gap-0 md:p-5 rounded-xl text-background md:text-center'>
                <Handshake
                  weight='duotone'
                  className='text-orange-600 md:mx-auto md:mb-2 size-8 xl:size-12'
                />
                <p className='text-base md:text-lg font-semibold leading-tight tracking-tight translate-y-1.5 md:translate-y-0'>
                  No spam. Just qualified dealflow
                </p>
              </div>
              <div className='flex items-start gap-3 p-3 bg-white md:block md:gap-0 md:p-5 rounded-xl text-background md:text-center'>
                <Files
                  weight='duotone'
                  className='text-orange-600 md:mx-auto md:mb-2 size-8 xl:size-12'
                />
                <p className='text-base md:text-lg font-semibold leading-tight tracking-tight translate-y-1.5 md:translate-y-0'>
                  No pitch deck = No Outreach
                </p>
              </div>
            </div>
            <Button
              type='button'
              onClick={handleInvestorTrialQX}
              className='font-medium w-full mt-4 inline-flex items-center px-7 py-3 text-sm rounded-lg !h-auto text-white bg-gradient-to-l from-orange-1 to-red-1'
            >
              Investors can Register with us here
              <ArrowRight weight='bold' className='ml-3 size-5' />
            </Button>
          </div>
        </div>
      </section>

      <section>
        <div className='container py-10 lg:py-20'>
          <div className='max-w-5xl mx-auto'>
            <h2 className='text-3xl font-semibold tracking-tight text-center lg:text-5xl font-display'>
              Trusted by the Region&apos;s Top Investors
            </h2>
          </div>
          <div className='relative flex w-full mt-8'>
            <div className='absolute inset-y-0 left-0 flex items-center w-full pl-3 border-none pointer-events-none text-white/40 sm:text-sm '>
              <MagnifyingGlass
                className='w-4 h-4 text-white/40'
                aria-hidden='true'
              />
            </div>
            <form className='relative w-full' onSubmit={handleInvestorSearch}>
              <input
                type='text'
                placeholder='Search Investors...'
                className='w-full h-12 p-3 pr-10 text-sm text-white transition-colors duration-200 border-none rounded-lg placeholder:text-white/40 pl-9 focus:outline-none hover:bg-white/20 bg-white/15'
                value={investorKeyword}
                onChange={(e) => setInvestorKeyword(e.target.value)}
              />
              {investorKeyword && (
                <button
                  type='button'
                  className='absolute -translate-y-1/2 right-3 top-1/2 text-white/40 hover:text-white'
                  onClick={resetInvestorSearch}
                >
                  <X className='w-4 h-4' />
                </button>
              )}
            </form>
          </div>
          {isSearchingInvestors ? (
            <div className='flex items-center justify-center my-6 text-center text-white'>
              <p>Searching...</p>
            </div>
          ) : investors.length === 0 ? (
            <div className='p-10 my-6 text-center text-white border border-dashed border-white/10 rounded-xl'>
              <div className='max-w-lg mx-auto'>
                <p className='mt-4 text-4xl font-medium tracking-tight font-display'>
                  No investors found
                </p>
                <p className='mt-3 text-sm opacity-70'>
                  Please adjust the search keyword or try again later.
                </p>
              </div>
            </div>
          ) : (
            <div className='grid mt-8 md:grid-cols-2 xl:grid-cols-4 gap-x-4 gap-y-12'>
              {investorGrid}
            </div>
          )}

          <div className='flex flex-col justify-center gap-2 mt-5 lg:mt-8 lg:flex-row'>
            <Button
              type='button'
              onClick={handleInvestorTrialQX}
              className='font-medium inline-flex items-center px-7 py-3 text-sm rounded-lg !h-auto text-white bg-gradient-to-l from-orange-1 to-red-1'
            >
              Click here if you&apos;re an investor
              <ArrowRight weight='bold' className='ml-3 size-5' />
            </Button>
          </div>
        </div>
      </section>

      <section className='bg-white/5'>
        <div className='container py-8 text-center lg:py-12'>
          <h2 className='text-3xl font-semibold leading-none tracking-tight lg:text-5xl font-display'>
            What Do You Get with the Startup Package
          </h2>
          <div className='grid gap-3 mt-8 leading-snug tracking-tight lg:text-lg lg:font-medium lg:gap-2 lg:grid-cols-4'>
            <div className='flex flex-row items-start gap-3 text-left rounded-lg lg:items-center lg:gap-0 lg:p-5 lg:text-center lg:flex-col lg:bg-white/5'>
              <span className='inline-flex items-center justify-center mb-2 text-sm bg-green-500 rounded-full lg:text-2xl shrink-0 size-5 lg:size-10'>
                <Check weight='bold' />
              </span>
              Verified Startup Profile (featured on LucidityInsights.com)
            </div>
            <div className='flex flex-row items-start gap-3 text-left rounded-lg lg:items-center lg:gap-0 lg:p-5 lg:text-center lg:flex-col lg:bg-white/5'>
              <span className='inline-flex items-center justify-center mb-2 text-sm bg-green-500 rounded-full lg:text-2xl shrink-0 size-5 lg:size-10'>
                <Check weight='bold' />
              </span>
              Access to the Middle East & Africa’s Largest Investor Database
            </div>
            <div className='flex flex-row items-start gap-3 text-left rounded-lg lg:items-center lg:gap-0 lg:p-5 lg:text-center lg:flex-col lg:bg-white/5'>
              <span className='inline-flex items-center justify-center mb-2 text-sm bg-green-500 rounded-full lg:text-2xl shrink-0 size-5 lg:size-10'>
                <Check weight='bold' />
              </span>
              Opportunity for Spotlight in Lucidity Reports & Media
            </div>
            <div className='flex flex-row items-start gap-3 text-left rounded-lg lg:items-center lg:gap-0 lg:p-5 lg:text-center lg:flex-col lg:bg-white/5'>
              <span className='inline-flex items-center justify-center mb-2 text-sm bg-green-500 rounded-full lg:text-2xl shrink-0 size-5 lg:size-10'>
                <Check weight='bold' />
              </span>
              Data-backed Visibility to 750+ Investors
            </div>
            <div className='flex flex-row items-start gap-3 text-left rounded-lg lg:items-center lg:gap-0 lg:p-5 lg:text-center lg:flex-col lg:bg-white/5'>
              <span className='inline-flex items-center justify-center mb-2 text-sm bg-green-500 rounded-full lg:text-2xl shrink-0 size-5 lg:size-10'>
                <Check weight='bold' />
              </span>
              “Investor-Ready” Verification Badge
            </div>
            <div className='flex flex-row items-start gap-3 text-left rounded-lg lg:items-center lg:gap-0 lg:p-5 lg:text-center lg:flex-col lg:bg-white/5'>
              <span className='inline-flex items-center justify-center mb-2 text-sm bg-green-500 rounded-full lg:text-2xl shrink-0 size-5 lg:size-10'>
                <Check weight='bold' />
              </span>
              Inclusion in Quarterly Founder–Investor Matchmaking Events
            </div>
            <div className='flex flex-row items-start gap-3 text-left rounded-lg lg:items-center lg:gap-0 lg:p-5 lg:text-center lg:flex-col lg:bg-white/5'>
              <span className='inline-flex items-center justify-center mb-2 text-sm bg-green-500 rounded-full lg:text-2xl shrink-0 size-5 lg:size-10'>
                <Check weight='bold' />
              </span>
              Special Offers from our Partners (Events, Discount SaaS Tools,
              etc.)
            </div>
            <div className='flex flex-row items-start gap-3 text-left rounded-lg lg:items-center lg:gap-0 lg:p-5 lg:text-center lg:flex-col lg:bg-white/5'>
              <span className='inline-flex items-center justify-center mb-2 text-sm bg-green-500 rounded-full lg:text-2xl shrink-0 size-5 lg:size-10'>
                <Check weight='bold' />
              </span>
              Access to all of our Educational Content
            </div>
          </div>
          <div className='flex flex-col items-center gap-8 mt-10 lg:gap-12 lg:flex-row lg:mt-20'>
            <div className='text-left lg:w-1/2'>
              <p className='text-3xl font-semibold leading-none tracking-tight lg:text-5xl font-display'>
                {`Investor's Top Fundraising Advice for Startup Founders`}
              </p>
              <p className='mt-4 text-sm lg:mt-6 opacity-60'>
                We spoke to over a dozen of the Middle East & Africa’s top
                Investors, and this is the top advice they have for Startup
                Founders preparing to Fundraise.
              </p>
              <div className='flex flex-col gap-2 mt-6 lg:mt-8 lg:flex-row'>
                <Button
                  type='button'
                  onClick={handleStartupTrialQ0}
                  className='font-medium inline-flex items-center px-7 py-3 text-sm rounded-lg !h-auto text-white bg-gradient-to-l from-orange-1 to-red-1'
                >
                  Start Pitching to Investors today!
                  <ArrowRight weight='bold' className='ml-3 size-5' />
                </Button>
              </div>
            </div>
            <div className='relative order-first lg:w-1/2 lg:order-last'>
              <ReactPlayer
                width='100%'
                height='100%'
                url={"https://cdn.lucidityinsights.com/media/startup-vid2.mp4"}
                playing={isPlayingPodcast}
                controls={isPlayingPodcast}
                className='overflow-hidden rounded-2xl'
              />
              {!isPlayingPodcast && (
                <>
                  <span className='absolute inset-0 w-full h-full bg-gradient-to-b from-[#161617] via-transparent to-[#161617] transition-opacity duration-500'></span>
                  <span className='absolute inset-0 w-full h-full bg-gradient-to-r from-[#161617] via-transparent to-[#161617] transition-opacity duration-500'></span>

                  <button
                    type='button'
                    onClick={() => setIsPlayingPodcast(true)}
                    className='absolute flex items-center justify-center gap-3 text-sm font-medium text-white -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2'
                  >
                    <span className='flex items-center justify-center rounded-full shadow-lg size-20 bg-gradient-to-tr from-orange-1 to-red-1'>
                      <Play weight='fill' className='size-10' />
                    </span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className='my-10 lg:my-20'>
          <p className='mb-5 text-base font-medium text-center lg:text-lg lg:mb-8'>
            Featured In
          </p>
          <div className='flex flex-wrap justify-center gap-4 lg:gap-12 lg:flex-nowrap'>
            {trusted_logos.map((logo, index) => (
              <Image
                key={index}
                src={logo.src}
                alt='Slider Image'
                className={`w-auto ${logo.height ?? "h-6 lg:h-7"}`}
                width={85}
                height={50}
              />
            ))}
          </div>
        </div>
      </section>

      <section className='max-w-5xl px-4 mx-auto'>
        <div className='relative w-full p-4 py-6 overflow-hidden text-white lg:px-8 lg:py-10 bg-gradient-to-tr from-purple-1 via-red-1 to-yellow-1 rounded-2xl'>
          <svg
            className='absolute bottom-0 object-cover rotate-[163deg] translate-x-10 opacity-50 scale-150'
            viewBox='0 0 723 542'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <g clipPath='url(#clip0_3829_2406)'>
              <mask
                id='mask0_3829_2406'
                style={{ maskType: "alpha" }}
                maskUnits='userSpaceOnUse'
                x='0'
                y='-181'
                width='723'
                height='723'
              >
                <rect y='-181' width='723' height='723' fill='#D9D9D9' />
              </mask>
              <g mask='url(#mask0_3829_2406)'>
                <g filter='url(#filter0_d_3829_2406)'>
                  <circle
                    cx='178.196'
                    cy='-61.5215'
                    r='291.5'
                    transform='rotate(-120 178.196 -61.5215)'
                    fill='url(#paint0_linear_3829_2406)'
                  />
                </g>
                <g filter='url(#filter1_d_3829_2406)'>
                  <circle
                    cx='192.837'
                    cy='-116.163'
                    r='291.5'
                    transform='rotate(-120 192.837 -116.163)'
                    fill='url(#paint1_linear_3829_2406)'
                  />
                </g>
                <g filter='url(#filter2_d_3829_2406)'>
                  <circle
                    cx='216.139'
                    cy='-175.804'
                    r='291.5'
                    transform='rotate(-120 216.139 -175.804)'
                    fill='url(#paint2_linear_3829_2406)'
                  />
                </g>
              </g>
            </g>
            <defs>
              <filter
                id='filter0_d_3829_2406'
                x='-133.352'
                y='-371.07'
                width='623.097'
                height='623.097'
                filterUnits='userSpaceOnUse'
                colorInterpolationFilters='sRGB'
              >
                <feFlood floodOpacity='0' result='BackgroundImageFix' />
                <feColorMatrix
                  in='SourceAlpha'
                  type='matrix'
                  values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                  result='hardAlpha'
                />
                <feOffset dy='2' />
                <feGaussianBlur stdDeviation='10' />
                <feComposite in2='hardAlpha' operator='out' />
                <feColorMatrix
                  type='matrix'
                  values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0'
                />
                <feBlend
                  mode='normal'
                  in2='BackgroundImageFix'
                  result='effect1_dropShadow_3829_2406'
                />
                <feBlend
                  mode='normal'
                  in='SourceGraphic'
                  in2='effect1_dropShadow_3829_2406'
                  result='shape'
                />
              </filter>
              <filter
                id='filter1_d_3829_2406'
                x='-118.711'
                y='-425.711'
                width='623.097'
                height='623.097'
                filterUnits='userSpaceOnUse'
                colorInterpolationFilters='sRGB'
              >
                <feFlood floodOpacity='0' result='BackgroundImageFix' />
                <feColorMatrix
                  in='SourceAlpha'
                  type='matrix'
                  values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                  result='hardAlpha'
                />
                <feOffset dy='2' />
                <feGaussianBlur stdDeviation='10' />
                <feComposite in2='hardAlpha' operator='out' />
                <feColorMatrix
                  type='matrix'
                  values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0'
                />
                <feBlend
                  mode='normal'
                  in2='BackgroundImageFix'
                  result='effect1_dropShadow_3829_2406'
                />
                <feBlend
                  mode='normal'
                  in='SourceGraphic'
                  in2='effect1_dropShadow_3829_2406'
                  result='shape'
                />
              </filter>
              <filter
                id='filter2_d_3829_2406'
                x='-95.4097'
                y='-485.352'
                width='623.097'
                height='623.097'
                filterUnits='userSpaceOnUse'
                colorInterpolationFilters='sRGB'
              >
                <feFlood floodOpacity='0' result='BackgroundImageFix' />
                <feColorMatrix
                  in='SourceAlpha'
                  type='matrix'
                  values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                  result='hardAlpha'
                />
                <feOffset dy='2' />
                <feGaussianBlur stdDeviation='10' />
                <feComposite in2='hardAlpha' operator='out' />
                <feColorMatrix
                  type='matrix'
                  values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0'
                />
                <feBlend
                  mode='normal'
                  in2='BackgroundImageFix'
                  result='effect1_dropShadow_3829_2406'
                />
                <feBlend
                  mode='normal'
                  in='SourceGraphic'
                  in2='effect1_dropShadow_3829_2406'
                  result='shape'
                />
              </filter>
              <linearGradient
                id='paint0_linear_3829_2406'
                x1='232.718'
                y1='-312.767'
                x2='-113.304'
                y2='-312.767'
                gradientUnits='userSpaceOnUse'
              >
                <stop stopColor='#e14f4f' />
                <stop offset='0.800681' stopColor='#E14F4F' />
                <stop offset='1' stopColor='#fdaf3a' />
              </linearGradient>
              <linearGradient
                id='paint1_linear_3829_2406'
                x1='247.36'
                y1='-367.408'
                x2='-98.6625'
                y2='-367.408'
                gradientUnits='userSpaceOnUse'
              >
                <stop stopColor='#e14f4f' />
                <stop offset='0.800681' stopColor='#E14F4F' />
                <stop offset='1' stopColor='#fdaf3a' />
              </linearGradient>
              <linearGradient
                id='paint2_linear_3829_2406'
                x1='270.661'
                y1='-427.049'
                x2='-75.3612'
                y2='-427.049'
                gradientUnits='userSpaceOnUse'
              >
                <stop stopColor='#e14f4f' />
                <stop offset='0.800681' stopColor='#E14F4F' />
                <stop offset='1' stopColor='#fdaf3a' />
              </linearGradient>
              <clipPath id='clip0_3829_2406'>
                <rect width='723' height='542' fill='white' />
              </clipPath>
            </defs>
          </svg>
          <div className='relative flex flex-col items-center text-center'>
            <p className='text-2xl font-semibold leading-tight tracking-tight capitalize lg:text-4xl font-display'>
              Ready to Meet Your Perfect Investor Match?
            </p>
            <p className='max-w-2xl mx-auto mt-3'>
              Join our platform that’s reshaping how investors connect in the
              Middle East, Africa, South Asia and Turkey.
            </p>
            <Button
              onClick={handleStartupTrialQ1}
              className='mt-5 px-7 py-3 text-sm rounded-lg !h-auto bg-white text-black'
            >
              Publish Your Startup Profile Today
            </Button>

            <p className='mt-3 text-xs italic opacity-80'>
              It takes less than 10 minutes to get started
            </p>
          </div>
        </div>
      </section>

      <section className='relative z-10 flex flex-col items-center gap-8 px-4 mx-auto my-10 max-w-7xl lg:gap-40 lg:my-20 lg:flex-row'>
        <div className='relative lg:w-2/5' id='angel-investor-plan'>
          <div className='hidden lg:block top-[6%] flex-shrink-0 absolute w-36 -right-[90px] scale-x-[-1] rotate-[23deg] aspect-[199/320]'>
            <Image
              src={LucidMascot.src}
              alt=''
              layout={"fill"}
              objectFit={"cover"}
            />
          </div>
          <div className='relative px-4 py-4 bg-white border border-white lg:py-6 lg:px-7 text-background rounded-xl'>
            <h3 className='text-sm font-medium leading-6'>
              Startup Registration Plan
            </h3>
            <div className='flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between lg:flex-col lg:items-stretch'>
              <div className='flex items-center mt-2 gap-x-3'>
                <p className='text-3xl font-bold tracking-tight text-black uppercase xl:text-4xl lg:text-5xl'>
                  <>
                    {currencyCode === "USD" ? "$" : currencyCode + " "}
                    {priceData["Startup Registration"]
                      ? convertStripeAmount(
                        priceData["Startup Registration"].unit_amount,
                        currencyCode
                      ).toLocaleString()
                      : "0"}
                  </>
                </p>
              </div>
              <div className='-mt-4'>
                <p className='text-sm opacity-50'>
                  Billed yearly{" "}
                  <span className='text-xs'>
                    {priceData["Startup Registration"] && (
                      <>
                        ({currencyCode === "USD" ? "$" : currencyCode + " "}
                        {(
                          priceData["Startup Registration"].unit_amount / 1200
                        ).toLocaleString("en", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        monthly)
                      </>
                    )}
                  </span>
                </p>
              </div>
            </div>
            <div className='flow-root mt-4 sm:mt-6'>
              <ul
                role='list'
                className='-my-1.5 text-sm leading-snug divide-y divide-background/5 border-background/5'
              >
                {startupBenefits.map((item, index) => (
                  <li className='flex py-1.5 gap-x-2.5' key={index}>
                    <svg
                      className='flex-none w-4 h-4 translate-y-0.5 text-orange-1'
                      viewBox='0 0 20 20'
                      fill='currentColor'
                      aria-hidden='true'
                    >
                      <path
                        fillRule='evenodd'
                        d='M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z'
                        clipRule='evenodd'
                      />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className='flex-1 order-first text-center lg:text-left lg:order-last'>
          <p className='text-3xl font-semibold tracking-tight text-white lg:text-5xl font-display'>
            Startup Founders: Your Next Big Opportunity Awaits!
          </p>
          <p className='mt-3 text-sm lg:text-base opacity-80 lg:mt-6'>
            {`Create your startup profile today to begin connecting with the regions most active venture capitalists and angel investors... and share your pitch deck directly with those investors you see a strategic fit with. Join now!`}
          </p>
          <Button
            onClick={handleStartupTrialQ0}
            size='lg'
            className='mt-4 lg:mt-6 font-medium inline-flex items-center px-7 py-3 text-sm rounded-lg !h-auto text-white bg-gradient-to-l from-orange-1 to-red-1'
          >
            Start {trialText} Free Trial
          </Button>
        </div>
      </section>

      <section className='relative'>
        <span className='absolute top-0 left-0 w-full h-full lg:w-1/2 bg-gradient-to-tr from-orange-500 to-purple-600'></span>
        <div className='absolute top-0 hidden w-1/2 h-full lg:flex left-1/2'>
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className='flex-1 h-full bg-gradient-to-tr from-background to-purple-500/20 lg:to-purple-500/10 backdrop-blur-2xl'
            ></div>
          ))}
        </div>
        <div className='container relative grid h-full gap-8 py-10 lg:gap-0 lg:py-16 lg:grid-cols-2 '>
          <div className='flex flex-col justify-center h-full text-center lg:text-left lg:pr-12'>
            <h1 className='text-3xl font-semibold leading-none tracking-tighter md:text-5xl'>
              Meet, Match and Fund <br /> your Startup
            </h1>

            {/* <p className='py-2 pl-4 mt-6 text-lg font-medium leading-tight tracking-tight border-t-2 border-white lg:border-l-2 bg-gradient-to-r from-white/20 to-transparent'>
              The ‘Tinder’ for startups and investors across the Middle East &
              Africa.
            </p> */}
            <p className='mt-3 text-sm leading-relaxed lg:mt-4 opacity-80'>
              Lucidity is your Startup Founder Stack: like having another strong
              co-founder, without giving away any equity.
            </p>
            <p className='mt-3 text-sm leading-relaxed lg:mt-4 opacity-80'>
              Our tools and resources, like our investor matchmaking tool, help
              you get the critical jobs done: like identifying the right
              investors for your business and doing the research you need to do,
              to connect, raise and scale your startup to reach the next level.
            </p>

            <div className='flex flex-col gap-2 mt-4 lg:mt-6 lg:flex-row'>
              <Button
                onClick={handleStartupTrialQ1}
                className='px-7 py-3 text-sm rounded-lg !h-auto text-white bg-background hover:bg-background/60'
              >
                Try our Startup Plan Free for {trialText.split(" ")[0]} Days
              </Button>
            </div>
          </div>
          <div className='relative flex flex-col items-center justify-center order-first lg:order-last'>
            <span className='absolute left-1/2 top-1/2 blur-3xl -translate-x-1/2 -translate-y-1/2 size-[320px] bg-gradient-to-tr from-orange-500 to-purple-600'></span>

            <div className='relative lg:ml-12'>
              {isMuted && (
                <button
                  onClick={() => setIsMuted(false)}
                  className='absolute z-10 inline-flex items-center justify-center font-medium -translate-x-1/2 -translate-y-1/2 rounded-full size-12 top-1/2 left-1/2 lg:size-16 border-background bg-gradient-to-tr from-yellow-1 to-red-1 pulse'
                >
                  <SpeakerHigh
                    weight='fill'
                    className='flex-shrink-0 text-2xl'
                  />
                </button>
              )}

              <div className='top-0 flex items-start relative aspect-[9/16] bg-black rounded-[53px] overflow-hidden w-1/2 mx-auto h-auto'>
                <Image src={videoCover} layout='fill' alt='' />

                <ReactPlayer
                  ref={videoRef}
                  url={
                    "https://cdn.lucidityinsights.com/media/startup-intro.mp4"
                  }
                  playing={true}
                  muted={isMuted}
                  controls={true}
                  loop={true}
                  width={"99%"}
                  height={"99%"}
                  style={{ marginLeft: "2px", marginTop: "2px" }}
                  stopOnUnmount={false}
                  pip
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <FreeTrialStartup
        openPurchasePopup={isPurchasePopup}
        setOpenPurchasePopup={setIsPurchasePopup}
        startingQuestion={startingQuestion}
        onOpenStartupRegistration={handleOpenStartupRegistration}
        onOpenContentAccess={handleOpenContentAccess}
      />

      <OnUpgradeAtStripeCheckout
        isOpen={isDirectFormOpen}
        setIsOpen={setIsDirectFormOpen}
        currencyCode={currencyCode}
        handleCheckboxChange={handleCheckboxChange}
        handleCheckCoupon={handleCheckCoupon}
        handleSubscription={handleSubscription}
        handleCouponChange={handleCouponChange}
        selectedPlan={selectedPlan}
        priceData={currentPriceData}
        isCoupon={isCoupon}
        coupon={coupon}
        couponError={couponError}
        setCouponError={setCouponError}
        onCancel={onCancel}
        inputCoupon={inputCoupon}
        loadingCheck={loadingCheck}
        couponErrorProduct={couponErrorProduct}
        onRemoveCoupon={onRemoveCoupon}
        planSubscription={planSubscription}
        priceDataMonthly={priceDataMonthly}
        billingCycle={billingCycle}
        setBillingCycle={setBillingCycle}
        user={user}
        emailPayloadForGuest={emailPayloadForGuest}
        setEmailPayloadForGuest={setEmailPayloadForGuest}
        fullNamePayloadForGuest={fullNamePayloadForGuest}
        phonePayloadForGuest={phonePayloadForGuest}
        hideNameEmail={true}
        showStartupFields={selectedPlan === "startup"}
        validateStartupInfo={validateStartupInfo}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
      />
    </Layout>
  );
};

export async function getServerSideProps({ req, query }) {
  // Import geolocation utility dynamically to avoid circular dependencies
  const { getGeolocationData, getUserIpFromRequest } = await import("@/utils/geolocation");

  const userIp = getUserIpFromRequest(req);

  // Fetch geolocation data using the shared utility (includes API key)
  const geoData = await getGeolocationData(userIp);

  // Create response object for compatibility with existing code
  const response = geoData.success ? { data: geoData } : null;

  const availablePrices =
    process.env.ENV !== "production" ? availablePricesDev : availablePricesProd;
  const availablePricesMonthly =
    process.env.ENV !== "production"
      ? availablePricesMonthlyDev
      : availablePricesMonthlyProd;

  let priceData = {};
  let priceDataMonthly = {};
  let currencyCode = "USD";
  let priceIds = availablePrices["USD"];
  let priceIdsMonthly = availablePricesMonthly["USD"];
  const codes = ["AED", "IDR", "JOD", "EGP", "SAR"];

  if (response?.data?.success && response?.data?.currency_code) {
    const currencyCodeFromApi = response.data.currency_code;
    if (codes.includes(currencyCodeFromApi)) {
      currencyCode = currencyCodeFromApi;
      priceIds = availablePrices[currencyCode];
      priceIdsMonthly = availablePricesMonthly[currencyCode];
    }
  }

  const stripe = require("stripe")(process.env.STRIPE_SECRET_KEY);

  // Fetch yearly pricing data
  for (const pKey in priceIds) {
    const stripePriceData = await stripe.prices.retrieve(priceIds[pKey]);
    priceData[pKey] = stripePriceData;
  }

  // Fetch monthly pricing data
  for (const pKey in priceIdsMonthly) {
    const stripePriceData = await stripe.prices.retrieve(priceIdsMonthly[pKey]);
    priceDataMonthly[pKey] = stripePriceData;
  }

  const seoResponse = await getSEOByPathname("-").catch((error) => {
    console.error("Error fetching SEO data:", error);
    return { data: {} };
  });

  const seoData = seoResponse?.data || {};

  return {
    props: {
      userLocation: response?.data || null,
      currencyCode,
      priceData,
      priceDataMonthly,
      priceIds,
      ip: userIp,
      seoData,
    },
  };
}

export default StartupIntro;
